import { useEffect, useRef, useState } from 'react';
import { RgbColorPicker, RgbColor } from 'react-colorful';

import { Svg } from '@/types/editProfile';

type SelectorType =
  | 'shieldModel'
  | 'shieldPattern'
  | 'tshirtModel'
  | 'tshirtBackground'
  | 'tshirtPattern'
  | 'tshirtColorPattern';

type colorPickerProps = {
  initialColor: string;
  label: string;
  values?: Svg;
  type: SelectorType;
  handleCustomizationValuesChange: (values: Svg) => void;
};

const rgbToHex = ({ r, g, b }: RgbColor): string => {
  const toHex = (n: number) => n.toString(16).padStart(2, '0');
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
};

const hexToRgb = (hex: string): RgbColor => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result
    ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16),
      }
    : { r: 0, g: 0, b: 0 };
};

const ColorPicker = ({
  label,
  values,
  initialColor,
  handleCustomizationValuesChange,
  type,
}: colorPickerProps) => {
  const [color, setColor] = useState<RgbColor>(() => hexToRgb(initialColor));
  const [isOpen, setIsOpen] = useState(false);
  const pickerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLDivElement>(null);

  const hexColor = rgbToHex(color);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        pickerRef.current &&
        !pickerRef.current.contains(event.target as Node) &&
        triggerRef.current &&
        !triggerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleColorChange = (newColor: RgbColor) => {
    setColor(newColor);
    handleValues();
  };

  const handleValues = () => {
    let updatedValues = { ...values };
    if (type === 'shieldModel') {
      updatedValues = {
        ...values,
        escudo: { ...values?.escudo, escudoparam4: hexColor.slice(1) },
      };
    } else if (type === 'shieldPattern') {
      updatedValues = {
        ...values,
        escudo: { ...values?.escudo, escudoparam3: hexColor.slice(1) },
      };
    } else if (type === 'tshirtBackground') {
      updatedValues = {
        ...values,
        camiseta: { ...values?.camiseta, param3: hexColor.slice(1) },
      };
    } else if (type === 'tshirtColorPattern') {
      updatedValues = {
        ...values,
        camiseta: { ...values?.camiseta, param4: hexColor.slice(1) },
      };
    }
    handleCustomizationValuesChange(updatedValues);
  };

  return (
    <div className="lg:py-4 lg:px-0  p-4  flex justify-between items-center relative">
      <p className="font-bold">{label}</p>
      {/* Color trigger square */}
      <div
        ref={triggerRef}
        aria-label="Open color picker"
        className="w-[38px] h-[38px] rounded-md cursor-pointer border border-gray-200 shadow-sm"
        style={{ backgroundColor: hexColor }}
        onClick={() => setIsOpen(!isOpen)}
      />
      {/* Floating color picker */}
      {isOpen && (
        <div
          ref={pickerRef}
          className="absolute z-50 p-4 bg-white rounded-xl shadow-lg w-[264px] h-[304px]"
        >
          <div className="space-y-4">
            <RgbColorPicker
              className="!w-[232px] !h-[203px]"
              color={color}
              onChange={handleColorChange}
            />
            <div className="flex items-center justify-between pt-2">
              <code className="text-sm font-mono text-gray-600">
                {hexColor}
              </code>
              <div
                className="w-8 h-8 rounded-lg border border-gray-200"
                style={{ backgroundColor: hexColor }}
              />
              <button
                className="bg-blue-500 hover:bg-blue-600 text-white px-6 py-2 rounded-lg"
                onClick={() => {
                  handleValues();
                  setIsOpen(false);
                }}
              >
                Aceptar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ColorPicker;
